import { useState, useEffect, useRef, useCallback } from "react";

/**
 * useNexiVoice - Voice recognition hook for Nexi AI
 * Uses Web Speech API with iOS Safari fallback handling
 * 
 * States: idle, listening, processing, error, unsupported
 */
export function useNexiVoice() {
  const [voiceState, setVoiceState] = useState("idle");
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const isInitialized = useRef(false);

  // Check support on mount
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    // iOS Safari in PWA mode has limited support
    const isIOSPWA = window.navigator.standalone === true;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS && isIOSPWA) {
      // iOS PWA mode - speech recognition often doesn't work
      setIsSupported(false);
      return;
    }

    setIsSupported(true);
  }, []);

  // Initialize recognition lazily (on first use)
  const initRecognition = useCallback(() => {
    if (isInitialized.current && recognitionRef.current) {
      return recognitionRef.current;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      return null;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false; // Single utterance for iOS compatibility
      recognition.interimResults = true;
      recognition.lang = "en-US";
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        console.log("Speech recognition started");
        setVoiceState("listening");
        setError(null);
      };

      recognition.onresult = (event) => {
        let interim = "";
        let final = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            final += result[0].transcript;
          } else {
            interim += result[0].transcript;
          }
        }

        if (final) {
          setTranscript((prev) => prev + final);
          setInterimTranscript("");
        } else {
          setInterimTranscript(interim);
        }

        // Reset silence timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          if (recognitionRef.current) {
            try {
              recognitionRef.current.stop();
            } catch (e) {
              // Ignore
            }
          }
        }, 3000);
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        
        let errorMessage = "";
        let shouldShowError = true;
        
        switch (event.error) {
          case "not-allowed":
            errorMessage = "Microphone access denied. Please allow microphone access in Settings.";
            break;
          case "service-not-allowed":
            errorMessage = "Voice input is not available in this app. Please type your message instead.";
            // Don't show as error state, just inform
            setIsSupported(false);
            break;
          case "no-speech":
            errorMessage = "No speech detected. Tap to try again.";
            shouldShowError = false;
            break;
          case "audio-capture":
            errorMessage = "No microphone found. Please check your device.";
            break;
          case "network":
            errorMessage = "Network error. Please check your connection.";
            break;
          case "aborted":
            // User cancelled
            setVoiceState("idle");
            return;
          default:
            errorMessage = "Voice input unavailable. Please type instead.";
        }
        
        if (shouldShowError) {
          setError(errorMessage);
          setVoiceState("error");
        } else {
          setVoiceState("idle");
        }
        
        // Clear error after delay
        setTimeout(() => {
          setError(null);
          if (voiceState !== "idle") {
            setVoiceState("idle");
          }
        }, 4000);
      };

      recognition.onend = () => {
        console.log("Speech recognition ended");
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        // Don't change state if we're already in error
        if (voiceState !== "error") {
          setVoiceState("idle");
        }
      };

      recognitionRef.current = recognition;
      isInitialized.current = true;
      return recognition;
    } catch (e) {
      console.error("Failed to initialize speech recognition:", e);
      setIsSupported(false);
      return null;
    }
  }, [voiceState]);

  const startListening = useCallback(() => {
    // Clear previous state
    setTranscript("");
    setInterimTranscript("");
    setError(null);

    if (!isSupported) {
      setError("Voice input is not available in this app. Please type your message instead.");
      setVoiceState("error");
      setTimeout(() => {
        setError(null);
        setVoiceState("idle");
      }, 3000);
      return;
    }

    const recognition = initRecognition();
    if (!recognition) {
      setError("Voice input is not available. Please type instead.");
      setVoiceState("error");
      setTimeout(() => {
        setError(null);
        setVoiceState("idle");
      }, 3000);
      return;
    }

    try {
      // Stop any existing session first
      try {
        recognition.stop();
      } catch (e) {
        // Ignore
      }
      
      // Small delay before starting new session
      setTimeout(() => {
        try {
          recognition.start();
        } catch (e) {
          console.error("Failed to start recognition:", e);
          setError("Could not start voice input. Please try again.");
          setVoiceState("error");
          setTimeout(() => {
            setError(null);
            setVoiceState("idle");
          }, 3000);
        }
      }, 100);
    } catch (e) {
      console.error("Failed to start recognition:", e);
      setError("Voice input failed. Please type instead.");
      setVoiceState("error");
    }
  }, [isSupported, initRecognition]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;

    try {
      recognitionRef.current.stop();
      setVoiceState("idle");
    } catch (e) {
      setVoiceState("idle");
    }
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscript("");
    setInterimTranscript("");
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore
        }
      }
    };
  }, []);

  return {
    voiceState,
    transcript,
    interimTranscript,
    fullTranscript: transcript + interimTranscript,
    startListening,
    stopListening,
    clearTranscript,
    error,
    isSupported
  };
}

export default useNexiVoice;
